<script setup>
import { Link } from '@inertiajs/vue3';
import { useBrowserLocation } from '@vueuse/core';
import Tags from './Partials/Tags.vue';
import Meta from './Partials/Meta.vue';

const browserLocation = useBrowserLocation();

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    image: {
        type: Object,
        default: null,
    },
    url: {
        type: String,
        default: null,
    },
    businessAreas: {
        type: Array,
        default: () => [],
    },
    topics: {
        type: Array,
        default: () => [],
    },
    advertorial: {
        type: Boolean,
        default: false,
    },
    contributors: {
        type: Array,
        default: () => [],
    },
    type: {
        type: String,
        default: null,
    },
    attendeeCount: {
        type: Number,
        default: null,
    },
    date: {
        type: String,
        default: null,
    },
    startDate: {
        type: String,
        default: null,
    },
    endDate: {
        type: String,
        default: null,
    },
    repetitionDatesCount: {
        type: Number,
        default: null,
    },
    location: {
        type: String,
        default: null,
    },
    variant: {
        type: String,
        default: null,
    },
    showCreditLine: {
        type: Boolean,
        default: false,
    },
});

const isExternal = !props.url?.includes(browserLocation.value.origin);
</script>

<template>
    <div>
        <component
            :is="url ? (isExternal ? 'a' : Link) : 'div'"
            :href="url ?? null"
            :target="url ? (isExternal ? '_blank': '_self') : null"
            :rel="url ? (isExternal ? 'noopener noreferrer': '') : null"
            class="@container relative min-h-[22rem] lg:min-h-[28rem] flex flex-col justify-end bg-white group rounded-xl"
        >
            <div
                v-if="image"
                class="absolute inset-0 overflow-hidden rounded-xl"
            >
                <picture v-if="image.avif">
                    <source
                        :srcset="image.avif"
                        type="image/avif"
                    >
                    <source
                        :srcset="image.webp"
                        type="image/webp"
                    >
                    <img
                        class="size-full object-cover object-center group-hover:scale-110 transition-transform ease-in-out"
                        :src="image.jpg"
                        :alt="image.alt"
                        loading="lazy"
                    >
                </picture>
            </div>

            <div
                class="absolute inset-0 bg-gradient-to-t from-deep-teal-700 from-5% to-transparent to-60% rounded-xl"
            />

            <div
                v-if="image"
                class="overflow-hidden px-4 space-y-5 isolate"
            >
                <Tags
                    :type
                    :advertorial
                    :business-areas
                    :attendee-count
                />
            </div>

            <div
                class="@xl:w-5/6 flex flex-col p-4 space-y-4 isolate"
                :class="{
                    '@xl:w-full px-8': variant == 'link',
                }"
            >
                <h3
                    class="font-semibold text-lg/snug text-white line-clamp-3"
                    :class="{
                        'text-2xl/snug': variant == 'link',
                    }"
                    :title
                >
                    {{ title }}
                </h3>

                <Meta
                    :contributors
                    :date
                    :start-date
                    :end-date
                    :repetition-dates-count
                    :topics
                    :location
                    is-hero
                />
            </div>
        </component>

        <div
            v-if="showCreditLine && image?.creditLine"
            class="text-sm/snug text-gray-500 py-3 md:px-4"
        >
            {{ $t('components.creditLine.expandedPrefix') }} {{ image.creditLine }}
        </div>
    </div>
</template>
